import { ComponentIds, OutlinedComponent } from '@inter-ikea-core/core';
import { ThemeFontStyleTypeEnum } from '@inter-ikea-kompis/enums/lib';
import { KompisSheetFooter, KompisSheetFooterButtons } from '@inter-ikea-kompis/react-components';
import { ConflictComponent } from '@inter-ikea-storageone/range';
import { useCore } from 'contexts/core';
import { WidgetContextValue } from 'contexts/widget';
import { getConflicts, getSelectedEntity, resetConflicts } from 'ducks/core';
import { useAppDispatch } from 'ducks/hooks';
import { getSelectedGroupId } from 'ducks/products';
import { useIntl } from 'react-intl';
import { useSelector } from 'react-redux';
import styled from 'styled-components';
import Box from 'ui/Box';
import { SkapaTheme } from '@inter-ikea-kompis/themes';
import Body from 'ui/Typography/Body';
import { isMobile } from 'utils/platform';
import IllustrationIcon from './new_illustration.svg?react';
import { useBehaviorTracker } from 'contexts/behaviors';

export const MenuContent = () => {
  const IllustrationWrapper = styled.div`
    text-align: center;
    padding: ${({ theme }) => theme.space.xxl} 0;
  `;

  const conflicts = useSelector(getConflicts);
  const { formatMessage } = useIntl();

  if (conflicts.length <= 0) {
    return null;
  }

  return (
    <Box>
      <Body type={ThemeFontStyleTypeEnum.bodyMedium} p={isMobile() ? 'md' : 0}>
        {formatMessage({ id: conflicts[0].messages.info })}
      </Body>

      {!isMobile() && (
        <IllustrationWrapper>
          <IllustrationIcon />
        </IllustrationWrapper>
      )}
    </Box>
  );
};

export const ConflictFooter = () => {
  const { formatMessage } = useIntl();
  const dispatch = useAppDispatch();
  const coreApi = useCore();
  const conflicts = useSelector(getConflicts);
  const behaviorTracker = useBehaviorTracker();
  const selectedEntityProduct = useSelector(getSelectedEntity);
  const selectedGroupId = useSelector(getSelectedGroupId);
  const selectedEntity =
    selectedEntityProduct && coreApi.entities.find(selectedEntityProduct?.entityId);

  const removeConflict = () => {
    conflicts.forEach(conflict => {
      const entity = coreApi.entities.find(conflict.entityId);
      if (entity) {
        entity.removeComponent(ConflictComponent.id);
      }
    });
  };

  if (conflicts.length <= 0) {
    return null;
  }

  const ctx = {
    groupId: selectedGroupId,
    menuType: 'sheet',
    widgetId: 'conflict-sheet'
  } as WidgetContextValue;

  const track = behaviorTracker.withContext(ctx);

  const actions = {
    revertAction: () =>
      track.custom(() => {
        removeConflict();
        dispatch(resetConflicts());
      }),

    solveConflict: () =>
      track.custom(() => {
        conflicts.forEach(conflict => {
          conflict.solve.forEach((s: any) => {
            coreApi.products.performAction(s.action, s.params);
          });
        });

        if (selectedEntity && !selectedEntity.hasComponent(ComponentIds.OUTLINED_COMPONENT_ID)) {
          selectedEntity.addComponent(new OutlinedComponent());
        }
        dispatch(resetConflicts());
      })
  };
  return (
    <KompisSheetFooter theme={SkapaTheme}>
      <KompisSheetFooterButtons
        compact={isMobile() ? true : false}
        theme={SkapaTheme}
        primaryLabel={formatMessage({ id: conflicts[0].messages.yesButton })}
        secondaryLabel={formatMessage({ id: conflicts[0].messages.noButton })}
        onButtonClick={event => {
          if (event.detail.type === 'primaryButton') {
            actions.solveConflict();
          } else {
            actions.revertAction();
          }
        }}
        data-testid="conflict-buttons"
      />
    </KompisSheetFooter>
  );
};
